import { useState } from 'react';
import { AlertTriangle, Check, Inbox, Loader2, MessageSquare, Wallet, X } from 'lucide-react';
import type { AbsenceCalendarRow, AbsenceListItem, LeaveBalanceResponse } from '@ip/shared-types';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  ABSENCE_TYPE_META, absenceTypeLabel, formatDateRange, formatDays, formatDaysHours, initials,
} from '@/lib/hr';
import { t } from '@/i18n';

interface ApprovalQueueProps {
  items: AbsenceListItem[];
  /** Keyed by employeeId — remaining balance with this pending request already reserved. */
  balances: Record<string, LeaveBalanceResponse | null>;
  /** Daily norms per employee (for the Xd Yh format); defaults to 8. */
  norms?: Record<string, number>;
  /** Team calendar rows used for the conflict warnings. */
  teamRows: AbsenceCalendarRow[];
  /** Direct reports count (the "N" in "2 of N away"). */
  teamSize: number;
  busyId: string | null;
  onDecide: (item: AbsenceListItem, decision: 'approve' | 'reject', note?: string) => void;
}

/**
 * The manager's approval queue: each card shows the requester, the range and
 * working-day cost, the balance AFTER approval (pending requests are already
 * reserved server-side, so the current remaining IS the post-approval state)
 * and team-overlap warnings — the two look-ups managers otherwise do by hand.
 */
export function ApprovalQueue({ items, balances, norms, teamRows, teamSize, busyId, onDecide }: ApprovalQueueProps) {
  const [notesOpen, setNotesOpen] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const conflictsFor = (item: AbsenceListItem) => {
    const overlapping = new Map<string, string>();
    for (const row of teamRows) {
      if (row.employeeId === item.employeeId) continue;
      const hit = row.entries.some(
        (e) => e.id !== item.id && e.dateFrom <= item.dateTo && item.dateFrom <= e.dateTo,
      );
      if (hit) overlapping.set(row.employeeId, `${row.firstName} ${row.lastName}`);
    }
    return [...overlapping.values()];
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t('hr.team.queueTitle')}</CardTitle>
        {items.length > 0 && (
          <span className="bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-full border border-amber-500/40 px-2 py-0.5 text-xs font-medium tabular-nums">
            {items.length}
          </span>
        )}
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-center text-sm">
            <Inbox className="size-8 opacity-40" />
            <p>{t('hr.team.queueEmpty')}</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => {
              const meta = ABSENCE_TYPE_META[item.type];
              const balance = meta.pool ? balances[item.employeeId] : null;
              const norm = norms?.[item.employeeId] ?? 8;
              const conflicts = conflictsFor(item);
              const busy = busyId === item.id;
              const [first = '', last = ''] = item.employeeName.split(' ');
              return (
                <li key={item.id} className="rounded-lg border p-3">
                  <div className="flex flex-wrap items-start gap-3">
                    <Avatar className="size-9">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                        {initials(first, last)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold">{item.employeeName}</span>
                        <span className={cn('rounded-full border px-2 py-0.5 text-xs font-medium', meta.chip)}>
                          {absenceTypeLabel(item.type)}
                        </span>
                      </div>
                      <p className="text-sm">
                        {formatDateRange(item.dateFrom, item.dateTo)}
                        <span className="text-muted-foreground"> · {t('hr.team.workingDaysShort', { days: formatDays(item.days) })}</span>
                      </p>
                      {item.reason && (
                        <p className="text-muted-foreground truncate text-xs italic" title={item.reason}>
                          {item.reason}
                        </p>
                      )}
                      {balance && (
                        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                          <Wallet className="size-3.5" />
                          {t('hr.team.afterApproval', { amount: formatDaysHours(balance.remainingHours, norm) })}
                        </p>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn('size-8', notesOpen[item.id] && 'bg-accent')}
                        aria-label={t('hr.team.note')}
                        onClick={() => setNotesOpen((s) => ({ ...s, [item.id]: !s[item.id] }))}
                      >
                        <MessageSquare className="size-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={busy}
                        onClick={() => onDecide(item, 'reject', notes[item.id]?.trim() || undefined)}
                      >
                        {busy ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
                        {t('hr.team.reject')}
                      </Button>
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => onDecide(item, 'approve', notes[item.id]?.trim() || undefined)}
                      >
                        {busy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                        {t('hr.team.approve')}
                      </Button>
                    </div>
                  </div>

                  {conflicts.length > 0 && (
                    <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                      <span>
                        {t('hr.team.conflict', {
                          count: conflicts.length + 1,
                          total: Math.max(teamSize, conflicts.length + 1),
                          names: conflicts.join(', '),
                        })}
                      </span>
                    </div>
                  )}

                  {notesOpen[item.id] && (
                    <Textarea
                      className="mt-2"
                      rows={2}
                      placeholder={t('hr.team.notePlaceholder')}
                      value={notes[item.id] ?? ''}
                      onChange={(e) => setNotes((s) => ({ ...s, [item.id]: e.target.value }))}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
